import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Pagination,
  Popover,
  Space,
  Spin,
  Tag,
  Tabs,
  Typography,
  Upload,
  message,
} from "antd";
import {
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  InboxOutlined,
  LoadingOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RobotOutlined,
  SaveOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  addAiTrainingFaq,
  deleteAiTrainingFaq,
  deleteAiTrainingSource,
  getAiPrompting,
  getAiTrainingOverview,
  NOTEBOOK_TYPES,
  retrainAiNotebook,
  updateAiPrompting,
  uploadAiTrainingSources,
} from "app/services/ai-training.service";
import { fetchWard, editWard } from "app/redux/action/ward";
import PageHeader from "app/components/pageHeader/pageHeader";
import { useModuleActionPermissions } from "app/hooks/usePermission";
import "./index.css";

const { Text, Title } = Typography;
const { Dragger } = Upload;
const { TextArea } = Input;

const NOTEBOOK_META = {
  [NOTEBOOK_TYPES.officers]: {
    label: "AI Cán bộ",
    shortLabel: "Cán bộ",
    color: "#3f9d2f",
    tint: "#eff9ed",
    icon: <UserOutlined />,
  },
  [NOTEBOOK_TYPES.citizens]: {
    label: "AI Người dân",
    shortLabel: "Người dân",
    color: "#1677ff",
    tint: "#eff6ff",
    icon: <BookOutlined />,
  },
};

const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.ppt,.pptx,.txt";
const ACCEPTED_FILE_LABEL = "PDF, Word, PowerPoint hoặc TXT";
const LIST_PAGE_SIZE = 5;
const HISTORY_PAGE_SIZE = 10;
const MAX_UPLOAD_FILES = 10;
const SUGGESTIONS_TAB_KEY = "suggestions";
const PROMPTING_TAB_KEY = "prompting";

const OPERATIONAL_PROMPT_REFERENCE_ROWS = [
  {
    group: "Cấu hình",
    syntax: "{maxWords}",
    meaning:
      "Số từ tối đa của phần nội dung trả lời, không tính dòng tiêu đề.",
  },
  {
    group: "Phản ánh",
    syntax: "{createdInPeriod}",
    meaning: "Số phản ánh mới phát sinh trong kỳ báo cáo.",
  },
  {
    group: "Phản ánh",
    syntax: "{createdDeltaPct}",
    meaning:
      "Tỷ lệ phần trăm giữa số phản ánh kỳ này và kỳ trước(Ví dụ: Kỳ trước 100 phản ánh, kỳ này 20 phản ánh, tỉ lệ thay đổi là -80%)",
  },
  {
    group: "Phản ánh",
    syntax: "{processing}",
    meaning: "Số phản ánh đang xử lý.",
  },
  {
    group: "Phản ánh",
    syntax: "{closed}",
    meaning: "Số phản ánh đã đóng.",
  },
  {
    group: "AI Chatbot",
    syntax: "{citizen.totalQueries}",
    meaning: "Tổng số lượt hỏi của người dân trong kỳ báo cáo.",
  },
  {
    group: "Tin tức",
    syntax: "{newInPeriod}",
    meaning: "Số bài viết mới trong kỳ báo cáo.",
  },
  {
    group: "Tin tức",
    syntax: "{viewsInPeriod}",
    meaning: "Tổng lượt xem tin tức trong kỳ báo cáo.",
  },
  {
    group: "MiniApp",
    syntax: "{totalUsers}",
    meaning: "Tổng số người dùng MiniApp.",
  },
  {
    group: "MiniApp",
    syntax: "{newInPeriod}",
    meaning: "Số người dùng MiniApp mới trong kỳ báo cáo.",
  },
  {
    group: "MiniApp",
    syntax: "{followOaNewInPeriod}",
    meaning: "Số lượt quan tâm OA mới trong kỳ báo cáo.",
  },
  {
    group: "Broadcast",
    syntax: "{createdInPeriod}",
    meaning: "Số thông báo Broadcast được tạo mới trong kỳ báo cáo.",
  },
  {
    group: "Cam kết",
    syntax: "{signedInPeriod}",
    meaning: "Số cam kết được ký mới trong kỳ báo cáo.",
  },
  {
    group: "KPI cán bộ",
    syntax: "{best}",
    meaning:
      "Danh sách tối đa 3 cán bộ có nhiều tài khoản MiniApp cài mới nhất tại TDP phụ trách.",
  },
  {
    group: "KPI cán bộ",
    syntax: "{needsImprovement}",
    meaning:
      "Danh sách tối đa 3 cán bộ cần cải thiện theo số tài khoản MiniApp cài mới tại TDP phụ trách.",
  },
  {
    group: "Cảnh báo",
    syntax: "{alerts}",
    meaning:
      "Các cảnh cáo liên quan trong tuần qua.",
  },
];

const formatDate = (value) => {
  if (!value) return "Chưa có";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "Chưa có";
};

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const RESPONSE_DATA_KEYS = [
  "data",
  "Data",
  "payload",
  "Payload",
  "result",
  "Result",
];
const LIST_DATA_KEYS = [
  "items",
  "rows",
  "docs",
  "results",
  "list",
  "data",
  "Data",
];

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const hasAnyKey = (source, keys) =>
  isPlainObject(source) && keys.some((key) => source[key] !== undefined);

const getByPath = (source, path) =>
  String(path)
    .split(".")
    .reduce(
      (current, key) => (current == null ? undefined : current[key]),
      source,
    );

const getFirstDefined = (source, paths) => {
  for (const path of paths) {
    const value = getByPath(source, path);
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const toText = (value, fallback = "") => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return fallback;
};

const getFirstText = (source, paths, fallback = "") => {
  for (const path of paths) {
    const value = toText(getByPath(source, path));
    if (value) return value;
  }
  return fallback;
};

const getResponsePayloadKey = (source) =>
  RESPONSE_DATA_KEYS.find((key) => source[key] !== undefined);

const getResponseData = (response) => {
  let current = response;

  for (let index = 0; index < 4; index += 1) {
    if (!isPlainObject(current)) return current || {};
    if (hasAnyKey(current, ["sources", "faqs", "history", "notebook"])) break;

    const dataKey = getResponsePayloadKey(current);
    if (!dataKey) break;

    const next = current[dataKey];
    if (next === current || next === undefined || next === null) break;
    current = next;
  }

  return current || {};
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!isPlainObject(value)) return [];

  for (const key of LIST_DATA_KEYS) {
    if (Array.isArray(value[key])) return value[key];
  }

  return [];
};

const getCollection = (source, paths) => {
  if (Array.isArray(source)) return source;

  for (const path of paths) {
    const value = getByPath(source, path);
    const list = toArray(value);
    if (list.length || Array.isArray(value)) return list;
  }

  return [];
};

const getPagination = (
  source,
  collectionSource,
  fallbackPage,
  fallbackLimit,
  total,
) => {
  const pagination =
    getFirstDefined(source, [
      "historyPagination",
      "pagination.history",
      "pagination",
      "history.pagination",
      "trainingHistoryPagination",
    ]) ||
    getFirstDefined(collectionSource, ["pagination", "meta", "pageInfo"]) ||
    (hasAnyKey(source, [
      "page",
      "currentPage",
      "limit",
      "pageSize",
      "total",
      "totalItems",
      "totalDocs",
    ])
      ? source
      : null) ||
    {};

  return {
    ...pagination,
    page: Number(
      pagination.page || pagination.currentPage || fallbackPage || 1,
    ),
    limit: Number(pagination.limit || pagination.pageSize || fallbackLimit),
    total: Number(
      pagination.total ||
        pagination.totalItems ||
        pagination.totalDocs ||
        total ||
        0,
    ),
  };
};

const normalizeRecord = (record, fallbackPrefix, index) =>
  isPlainObject(record)
    ? record
    : {
        id: `${fallbackPrefix}-${index}`,
        name: toText(record, `${fallbackPrefix} ${index + 1}`),
      };

const normalizeSource = (source, index) => {
  const record = normalizeRecord(source, "source", index);
  const sourceId = getFirstDefined(record, [
    "sourceId",
    "id",
    "_id",
    "fileId",
    "documentId",
    "key",
  ]);
  const name = getFirstText(
    record,
    ["title", "name", "fileName", "filename", "originalName", "displayName"],
    `Tài liệu ${index + 1}`,
  );

  return {
    ...record,
    id: record.id || sourceId || `source-${index}`,
    sourceId: sourceId || record.sourceId,
    title: getFirstText(record, ["title"], name),
    name: getFirstText(record, ["name"], name),
    size: getFirstDefined(record, ["size", "fileSize", "bytes"]),
    uploadedAt: getFirstDefined(record, [
      "uploadedAt",
      "createdAt",
      "updatedAt",
    ]),
    uploadedByName: getFirstText(
      record,
      [
        "uploadedByName",
        "createdByName",
        "actorName",
        "userName",
        "uploadedBy.name",
        "createdBy.name",
      ],
      "Hệ thống",
    ),
  };
};

const normalizeFaq = (faq, index) => {
  const record = normalizeRecord(faq, "faq", index);
  const faqId = getFirstDefined(record, [
    "id",
    "_id",
    "faqId",
    "sourceId",
    "key",
  ]);

  return {
    ...record,
    id: faqId || `faq-${index}`,
    question: getFirstText(
      record,
      ["question", "title", "prompt", "name"],
      `FAQ ${index + 1}`,
    ),
    answer: getFirstText(
      record,
      ["answer", "response", "reply", "content", "detail"],
      "",
    ),
    createdAt: getFirstDefined(record, [
      "createdAt",
      "updatedAt",
      "uploadedAt",
    ]),
    createdByName: getFirstText(
      record,
      [
        "createdByName",
        "uploadedByName",
        "actorName",
        "userName",
        "createdBy.name",
      ],
      "Hệ thống",
    ),
  };
};

const normalizeHistoryItem = (item, index) => {
  const record = normalizeRecord(item, "history", index);
  const action = getFirstText(
    record,
    ["action", "type", "event", "kind"],
    "update",
  );

  return {
    ...record,
    id:
      getFirstDefined(record, ["id", "_id", "historyId", "logId"]) ||
      `history-${index}`,
    actorName: getFirstText(
      record,
      [
        "actorName",
        "createdByName",
        "userName",
        "actor.name",
        "createdBy.name",
      ],
      "Hệ thống",
    ),
    action,
    actionLabel: getFirstText(record, ["actionLabel", "label"], action),
    detail: getFirstText(
      record,
      ["detail", "message", "description", "content", "title"],
      "",
    ),
    createdAt:
      getFirstDefined(record, [
        "createdAt",
        "updatedAt",
        "time",
        "timestamp",
        "date",
      ]) || null,
  };
};

const normalizeOverviewResponse = (response, fallbackPage, fallbackLimit) => {
  const envelope = isPlainObject(response) ? response : {};
  const data = getResponseData(response);
  const paginationSource = {
    ...envelope,
    ...(isPlainObject(data) ? data : {}),
  };
  const sourceCollectionSource =
    getFirstDefined(data, [
      "sources",
      "sourceList",
      "trainingSources",
      "documents",
      "files",
    ]) || (Array.isArray(data) ? data : undefined);
  const faqCollectionSource = getFirstDefined(data, [
    "faqs",
    "faqList",
    "faqItems",
    "questions",
  ]);
  const historyCollectionSource = getFirstDefined(data, [
    "history",
    "histories",
    "logs",
    "trainingHistory",
  ]);
  const sources = getCollection(data, [
    "sources",
    "sourceList",
    "trainingSources",
    "documents",
    "files",
  ]).map(normalizeSource);
  const faqs = getCollection(data, [
    "faqs",
    "faqList",
    "faqItems",
    "questions",
  ]).map(normalizeFaq);
  const history = getCollection(data, [
    "history",
    "histories",
    "logs",
    "trainingHistory",
  ]).map(normalizeHistoryItem);

  return {
    sources: sources.length
      ? sources
      : toArray(sourceCollectionSource).map(normalizeSource),
    faqs: faqs.length ? faqs : toArray(faqCollectionSource).map(normalizeFaq),
    history: history.length
      ? history
      : toArray(historyCollectionSource).map(normalizeHistoryItem),
    historyPagination: getPagination(
      paginationSource,
      historyCollectionSource,
      fallbackPage,
      fallbackLimit,
      history.length || toArray(historyCollectionSource).length,
    ),
    notebook:
      getFirstDefined(data, [
        "notebook",
        "trainingNotebook",
        "settings",
        "meta.notebook",
      ]) || {},
  };
};

const normalizeUploadResult = (response, fallbackCount) => {
  const data = getResponseData(response);
  const uploaded = getCollection(data, [
    "uploaded",
    "uploadedFiles",
    "files",
    "sources",
    "documents",
    "items",
  ]);
  const replaced = getCollection(data, [
    "replaced",
    "replacements",
    "overwritten",
  ]);
  const uploadedCount =
    toNumber(
      getFirstDefined(data, [
        "uploadedCount",
        "successCount",
        "count",
        "totalUploaded",
      ]),
    ) ||
    uploaded.length ||
    fallbackCount;
  const replacedCount =
    toNumber(
      getFirstDefined(data, [
        "replacedCount",
        "replacementCount",
        "overwrittenCount",
      ]),
    ) ||
    replaced.length ||
    0;

  return { uploadedCount, replacedCount };
};

const getSensitiveUploadConfirmation = (response) => {
  const data = getResponseData(response);
  return data?.requiresConfirmation ? data : null;
};

const confirmSensitiveTrainingUpload = (scanResponse) =>
  new Promise((resolve) => {
    const warningDocuments = scanResponse?.documents || [];

    Modal.confirm({
      title: "Cảnh báo tài liệu cần kiểm tra",
      icon: <WarningOutlined style={{ color: "#faad14" }} />,
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            {scanResponse?.message ||
              "Tài liệu có dấu hiệu mật hoặc cần rà soát thủ công trước khi tải vào kho training AI."}
          </div>
          {warningDocuments.map((document, index) => (
            <div
              key={`${document.name}-${index}`}
              style={{
                padding: "8px 10px",
                border: "1px solid #ffe58f",
                borderRadius: 6,
                background: "#fffbe6",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {document.name}
              </div>
              {(document.securityScan?.indicators || []).map(
                (indicator, indicatorIndex) => (
                  <div key={indicatorIndex} style={{ fontSize: 13 }}>
                    {indicator}
                  </div>
                ),
              )}
            </div>
          ))}
        </div>
      ),
      okText: "Tiếp tục tải lên",
      cancelText: "Hủy",
      okButtonProps: { danger: true },
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

const getErrorMessage = (error, fallback) => {
  const data = getResponseData(error?.response?.data);
  return data?.message || data?.error || error?.message || fallback;
};

const normalizeFile = (file) => ({
  ...file,
  uid: file.uid || `${file.name}-${file.lastModified || Date.now()}`,
});

const getUploadFileNameKey = (file) =>
  String(file?.name || file?.originFileObj?.name || "")
    .normalize("NFC")
    .trim()
    .toLocaleLowerCase("vi-VN");

const dedupeSelectedFilesByName = (files = []) => {
  const fileByName = new Map();
  files.map(normalizeFile).forEach((file) => {
    const nameKey = getUploadFileNameKey(file);
    fileByName.set(nameKey || file.uid, file);
  });
  return [...fileByName.values()];
};

const getFileIcon = (name = "") => {
  if (/\.pdf$/i.test(name)) {
    return <FilePdfOutlined className="ai-training-file-icon is-pdf" />;
  }
  return <FileTextOutlined className="ai-training-file-icon" />;
};

const sourceDisplayName = (source) =>
  getFirstText(
    source,
    ["title", "name", "fileName", "filename", "originalName", "displayName"],
    "Tài liệu không tên",
  );

const PendingRetrainNotice = () => (
  <div className="ai-training-pending-retrain-notice">
    <ThunderboltOutlined />
    <span>
      Có thay đổi chờ huấn luyện lại. Bạn cần bấm Huấn luyện lại để áp dụng các
      thay đổi
    </span>
  </div>
);

const isFaqSource = (source, faqSourceId) =>
  (faqSourceId &&
    String(source?.sourceId || source?.id) === String(faqSourceId)) ||
  source?.kind === "faq" ||
  /^FAQ-(officers|citizens)(?:-|$)/i.test(sourceDisplayName(source));

const sortNewestFirst = (left, right, dateKeys) => {
  const getDate = (item) => {
    const value = dateKeys.map((key) => item?.[key]).find(Boolean);
    return new Date(value || 0).getTime() || 0;
  };

  return getDate(right) - getDate(left);
};

const isRetrainAction = (item) =>
  String(item?.action || item?.type || "").toLowerCase() === "retrain";

const getHistoryTime = (item) =>
  new Date(item?.createdAt || item?.updatedAt || 0).getTime() || 0;

const getFirstNumber = (source, keys) => {
  for (const key of keys) {
    const value = Number(source?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const getFirstValue = (source, keys) =>
  keys.map((key) => source?.[key]).find(Boolean);

const getVersionFromText = (text = "") => {
  const match = String(text).match(/(?:v|phiên bản)\s*(\d+)/i);
  return match ? Number(match[1]) : null;
};

const resolveTrainingSnapshot = (
  notebook = {},
  history = [],
  previousSnapshot,
) => {
  const sortedHistory = [...history].sort(
    (left, right) => getHistoryTime(right) - getHistoryTime(left),
  );
  const latestRetrain = sortedHistory.find(isRetrainAction);
  const latestRetrainAt = getHistoryTime(latestRetrain);
  const pendingChangeCount = sortedHistory.filter((item) => {
    if (isRetrainAction(item)) return false;
    const itemTime = getHistoryTime(item);
    return !latestRetrainAt || itemTime > latestRetrainAt;
  }).length;
  const hasPendingChanges = pendingChangeCount > 0;
  const explicitVersion = getFirstNumber(notebook, [
    "trainedVersion",
    "lastTrainedVersion",
    "lastTrainingVersion",
    "lastRetrainVersion",
    "activeTrainingVersion",
    "publishedTrainingVersion",
  ]);
  const explicitTrainedAt = getFirstValue(notebook, [
    "lastRetrainedAt",
    "lastRetrainAt",
    "lastTrainedAt",
  ]);

  if (explicitVersion !== null) {
    return {
      version: explicitVersion,
      trainedAt:
        explicitTrainedAt ||
        latestRetrain?.createdAt ||
        notebook?.lastTrainingAt,
      hasPendingChanges,
    };
  }

  if (hasPendingChanges && previousSnapshot) {
    return {
      ...previousSnapshot,
      hasPendingChanges,
    };
  }

  const apiVersion = getFirstNumber(notebook, ["trainingVersion"]) || 0;
  const retrainVersion =
    getFirstNumber(latestRetrain, [
      "version",
      "trainingVersion",
      "trainedVersion",
      "toVersion",
      "notebookVersion",
    ]) ?? getVersionFromText(latestRetrain?.detail);

  if (latestRetrain) {
    return {
      version: retrainVersion ?? Math.max(0, apiVersion - pendingChangeCount),
      trainedAt: latestRetrain.createdAt || explicitTrainedAt,
      hasPendingChanges,
    };
  }

  if (hasPendingChanges) {
    return {
      version: 0,
      trainedAt: null,
      hasPendingChanges,
    };
  }

  return {
    version: apiVersion,
    trainedAt: explicitTrainedAt || notebook?.lastTrainingAt,
    hasPendingChanges: false,
  };
};

const AiTraining = () => {
  const dispatch = useDispatch();
  const { canCreate, canUpdate, canDelete } =
    useModuleActionPermissions("AITraining");
  const { currentWard, loading: wardLoading } = useSelector(
    (state) => state.ward,
  );
  const [notebookType, setNotebookType] = useState(NOTEBOOK_TYPES.officers);
  const [activeTab, setActiveTab] = useState(NOTEBOOK_TYPES.officers);
  const [overview, setOverview] = useState({
    sources: [],
    faqs: [],
    history: [],
  });
  const [trainingSnapshots, setTrainingSnapshots] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [retraining, setRetraining] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadCommitmentChecked, setUploadCommitmentChecked] = useState(false);
  const [sourcePage, setSourcePage] = useState(1);
  const [faqPage, setFaqPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const faqListRef = useRef(null);
  const [faqForm] = Form.useForm();
  const [suggestionsForm] = Form.useForm();
  const [promptingForm] = Form.useForm();
  const [promptingLoading, setPromptingLoading] = useState(false);
  const [promptingSaving, setPromptingSaving] = useState(false);

  const meta = NOTEBOOK_META[notebookType];

  const loadOverview = useCallback(
    async (type = notebookType, page = 1) => {
      setLoading(true);
      try {
        const response = await getAiTrainingOverview(
          type,
          page,
          HISTORY_PAGE_SIZE,
        );
        const nextOverview = normalizeOverviewResponse(
          response,
          page,
          HISTORY_PAGE_SIZE,
        );

        setOverview(nextOverview);
        setTrainingSnapshots((current) => ({
          ...current,
          [type]: resolveTrainingSnapshot(
            nextOverview.notebook,
            nextOverview.history,
            current[type],
          ),
        }));
        setHistoryPage(nextOverview.historyPagination?.page || page);
      } catch (error) {
        message.error(
          getErrorMessage(error, "Không thể tải dữ liệu training AI"),
        );
      } finally {
        setLoading(false);
      }
    },
    [notebookType],
  );

  useEffect(() => {
    setFileList([]);
    setUploadCommitmentChecked(false);
    setSourcePage(1);
    setFaqPage(1);
    setHistoryPage(1);
    faqForm.resetFields();
    loadOverview(notebookType, 1);
  }, [faqForm, loadOverview, notebookType]);

  useEffect(() => {
    dispatch(fetchWard());
  }, [dispatch]);

  useEffect(() => {
    if (currentWard) {
      suggestionsForm.setFieldsValue({
        aiSuggestedMessages: currentWard.aiSuggestedMessages || [],
      });
    }
  }, [currentWard, suggestionsForm]);

  const handleFilesChange = ({ fileList: nextFileList }) => {
    if (nextFileList.length > MAX_UPLOAD_FILES) {
      message.warning({
        key: "ai-training-max-files",
        content: `Chỉ được tải tối đa ${MAX_UPLOAD_FILES} tài liệu trong một lần. Hệ thống đã giữ ${MAX_UPLOAD_FILES} file đầu tiên trong danh sách chọn.`,
      });
    }

    const normalizedFileList = dedupeSelectedFilesByName(nextFileList).slice(
      0,
      MAX_UPLOAD_FILES,
    );
    setFileList(normalizedFileList);
    if (!normalizedFileList.length) {
      setUploadCommitmentChecked(false);
    }
  };

  const handleUpload = async () => {
    const files = fileList
      .map((item) => item.originFileObj || item)
      .filter(Boolean);
    if (!files.length) {
      message.warning("Vui lòng chọn ít nhất một tài liệu");
      return;
    }
    if (files.length > MAX_UPLOAD_FILES) {
      message.warning({
        key: "ai-training-max-files",
        content: `Chỉ được tải tối đa ${MAX_UPLOAD_FILES} tài liệu trong một lần. Vui lòng bớt file rồi tải lại.`,
      });
      return;
    }
    if (!uploadCommitmentChecked) {
      message.warning("Vui lòng tích cam kết trước khi tải tài liệu lên");
      return;
    }

    setUploading(true);
    try {
      let uploadResponse = await uploadAiTrainingSources(notebookType, files);
      const confirmation = getSensitiveUploadConfirmation(uploadResponse);

      if (confirmation) {
        const shouldContinue =
          await confirmSensitiveTrainingUpload(confirmation);
        if (!shouldContinue) {
          message.info("Đã hủy tải tài liệu training lên hệ thống.");
          return;
        }

        uploadResponse = await uploadAiTrainingSources(
          notebookType,
          files,
          undefined,
          true,
        );
      }

      const { uploadedCount, replacedCount } = normalizeUploadResult(
        uploadResponse,
        files.length,
      );
      message.success(
        replacedCount
          ? `Đã tải ${uploadedCount} tài liệu, trong đó ${replacedCount} file trùng tên đã thay thế bản cũ. Bấm Huấn luyện lại để cập nhật phiên bản AI.`
          : `Đã tải ${uploadedCount} tài liệu. Bấm Huấn luyện lại để cập nhật phiên bản AI.`,
      );
      setFileList([]);
      setUploadCommitmentChecked(false);
      setHistoryPage(1);
      await loadOverview(notebookType, 1);
    } catch (error) {
      message.error(getErrorMessage(error, "Tải tài liệu training thất bại"));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteSource = async (source) => {
    try {
      await deleteAiTrainingSource(notebookType, source.sourceId || source.id);
      message.success(
        "Đã xóa tài liệu. Bấm Huấn luyện lại để cập nhật phiên bản AI.",
      );
      setHistoryPage(1);
      await loadOverview(notebookType, 1);
    } catch (error) {
      message.error(getErrorMessage(error, "Không thể xóa tài liệu"));
    }
  };

  const handleAddFaq = async (values) => {
    try {
      await addAiTrainingFaq({ ...values, notebookType });
      message.success(
        "Đã thêm FAQ. Bấm Huấn luyện lại để cập nhật phiên bản AI.",
      );
      faqForm.resetFields();
      setFaqPage(1);
      setHistoryPage(1);
      await loadOverview(notebookType, 1);
    } catch (error) {
      message.error(getErrorMessage(error, "Không thể thêm FAQ"));
    }
  };

  const handleDeleteFaq = async (faq) => {
    try {
      await deleteAiTrainingFaq(notebookType, faq.id || faq._id);
      message.success(
        "Đã xóa FAQ. Bấm Huấn luyện lại để cập nhật phiên bản AI.",
      );
      setHistoryPage(1);
      await loadOverview(notebookType, 1);
    } catch (error) {
      message.error(getErrorMessage(error, "Không thể xóa FAQ"));
    }
  };

  const handleRetrain = async () => {
    setRetraining(true);
    try {
      await retrainAiNotebook(notebookType);
      message.success(`${meta.label} đã được huấn luyện lại`);
      setHistoryPage(1);
      await loadOverview(notebookType, 1);
    } catch (error) {
      message.error(getErrorMessage(error, "Huấn luyện lại thất bại"));
    } finally {
      setRetraining(false);
    }
  };

  const handleUpdateSuggestions = async () => {
    try {
      const values = await suggestionsForm.validateFields();
      if (!currentWard?.id) {
        message.error("Không tìm thấy cấu hình đơn vị!");
        return;
      }

      await dispatch(editWard(currentWard.id, values));
      await dispatch(fetchWard());
      message.success("Cập nhật tin nhắn đề xuất thành công!");
    } catch (error) {
      console.error("Update AI suggestions error:", error);
      message.error(error.message || "Có lỗi xảy ra khi cập nhật!");
    }
  };

  const loadPrompting = useCallback(async () => {
    setPromptingLoading(true);
    try {
      const res = await getAiPrompting();
      let prompting = {};
      if (Array.isArray(res)) {
        prompting = res[0] || {};
      } else if (Array.isArray(res?.data)) {
        prompting = res.data[0] || {};
      } else if (res?.data && typeof res.data === "object") {
        prompting = res.data;
      } else if (res && typeof res === "object") {
        prompting = res;
      }
      promptingForm.setFieldsValue({
        prompt: prompting.prompt || "",
        operationalAssistantPrompt:
          prompting.operationalAssistantPrompt || "",
      });
    } catch (error) {
      console.error("Load AI prompting error:", error);
      message.error(
        getErrorMessage(error, "Không thể tải nội dung prompting AI"),
      );
    } finally {
      setPromptingLoading(false);
    }
  }, [promptingForm]);

  const handleUpdatePrompting = async (values) => {
    setPromptingSaving(true);
    try {
      await updateAiPrompting(values);
      message.success("Cập nhật prompting thành công!");
    } catch (error) {
      console.error("Update AI prompting error:", error);
      message.error(
        getErrorMessage(error, "Có lỗi xảy ra khi cập nhật prompting!"),
      );
    } finally {
      setPromptingSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab === PROMPTING_TAB_KEY) {
      loadPrompting();
    }
  }, [activeTab, loadPrompting]);

  const notebookTabs = useMemo(
    () => [
      ...Object.values(NOTEBOOK_TYPES).map((type) => ({
        key: type,
        label: (
          <span className="ai-training-tab-label">
            {NOTEBOOK_META[type].icon}
            {NOTEBOOK_META[type].label}
          </span>
        ),
      })),
      {
        key: SUGGESTIONS_TAB_KEY,
        label: (
          <span className="ai-training-tab-label">
            <SettingOutlined />
            Tin nhắn đề xuất
          </span>
        ),
      },
      {
        key: PROMPTING_TAB_KEY,
        label: (
          <span className="ai-training-tab-label">
            <RobotOutlined />
            Prompting
          </span>
        ),
      },
    ],
    [],
  );

  const sources = useMemo(
    () =>
      [...(overview.sources || [])]
        .filter(
          (source) => !isFaqSource(source, overview.notebook?.faqSourceId),
        )
        .sort((left, right) =>
          sortNewestFirst(left, right, ["uploadedAt", "createdAt"]),
        ),
    [overview.sources, overview.notebook?.faqSourceId],
  );
  const faqs = useMemo(
    () =>
      [...(overview.faqs || [])].sort((left, right) =>
        sortNewestFirst(left, right, ["createdAt"]),
      ),
    [overview.faqs],
  );
  const history = overview.history || [];
  const sourceTotalPages = Math.max(
    1,
    Math.ceil(sources.length / LIST_PAGE_SIZE),
  );
  const faqTotalPages = Math.max(1, Math.ceil(faqs.length / LIST_PAGE_SIZE));
  const visibleSources = sources.slice(
    (sourcePage - 1) * LIST_PAGE_SIZE,
    sourcePage * LIST_PAGE_SIZE,
  );
  const visibleFaqs = faqs.slice(
    (faqPage - 1) * LIST_PAGE_SIZE,
    faqPage * LIST_PAGE_SIZE,
  );
  const historyPagination = overview.historyPagination || {};
  const historyTotal = Number(historyPagination.total || history.length);
  const historyPageSize = Number(historyPagination.limit || HISTORY_PAGE_SIZE);
  const trainingSnapshot =
    trainingSnapshots[notebookType] ||
    resolveTrainingSnapshot(overview.notebook, history);
  const isSuggestionsTab = activeTab === SUGGESTIONS_TAB_KEY;
  const isPromptingTab = activeTab === PROMPTING_TAB_KEY;
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key !== SUGGESTIONS_TAB_KEY && key !== PROMPTING_TAB_KEY) {
      setNotebookType(key);
    }
  };

  useEffect(() => {
    setSourcePage((current) => Math.min(current, sourceTotalPages));
  }, [sourceTotalPages]);

  useEffect(() => {
    setFaqPage((current) => Math.min(current, faqTotalPages));
  }, [faqTotalPages]);

  const handleFaqPageChange = (page) => {
    setFaqPage(page);
    requestAnimationFrame(() => {
      faqListRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    });
  };

  return (
    <div className="ai-training-page">
      <PageHeader className="ai-training-page-header" title="Training AI" />

      <Tabs
        className="ai-training-notebook-tabs"
        activeKey={activeTab}
        items={notebookTabs}
        onChange={handleTabChange}
      />

      {isSuggestionsTab ? (
        <Card
          className="ai-training-card ai-training-suggestions-card"
          title={
            <span className="ai-training-card-title">
              <SettingOutlined /> Tin nhắn đề xuất
            </span>
          }
        >
          <Text type="secondary" className="ai-training-helper-text">
            Các tin nhắn này sẽ hiển thị như gợi ý cho người dân khi bắt đầu trò
            chuyện với AI.
          </Text>

          <Form
            form={suggestionsForm}
            layout="vertical"
            onFinish={handleUpdateSuggestions}
            disabled={!canUpdate}
          >
            <Form.List name="aiSuggestedMessages">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div className="ai-training-suggestion-row" key={key}>
                      <Form.Item
                        {...restField}
                        name={name}
                        rules={[
                          {
                            required: true,
                            message: "Vui lòng nhập nội dung",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Ví dụ: Thủ tục cấp hộ chiếu như thế nào?"
                          size="large"
                        />
                      </Form.Item>
                      <Button
                        danger
                        aria-label="Xóa tin nhắn đề xuất"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </div>
                  ))}
                  {fields.length < 4 && (
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                        className="ai-training-add-suggestion"
                      >
                        Thêm tin nhắn đề xuất
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>

            <Divider />

            <div className="ai-training-form-actions">
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={wardLoading}
                disabled={!canUpdate}
              >
                Lưu thay đổi
              </Button>
            </div>
          </Form>
        </Card>
      ) : null}

      {isPromptingTab ? (
        <Card
          className="ai-training-card ai-training-prompting-card"
          title={
            <span className="ai-training-card-title">
              <RobotOutlined /> Prompting
            </span>
          }
        >
          <Spin spinning={promptingLoading}>
            <Form
              form={promptingForm}
              layout="vertical"
              onFinish={handleUpdatePrompting}
              disabled={!canUpdate}
            >
              <section className="ai-training-prompt-section">
                <Title level={5}>Phân tích chủ đề câu hỏi</Title>
                <Text type="secondary" className="ai-training-helper-text">
                  Prompt dùng để nhóm và tổng hợp các chủ đề người dùng quan
                  tâm.
                </Text>
                <Form.Item
                  name="prompt"
                  label="Nội dung Prompt"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng nhập nội dung prompt",
                    },
                  ]}
                >
                  <TextArea
                    rows={10}
                    placeholder="Nhập prompt phân tích chủ đề câu hỏi..."
                    maxLength={10000}
                    showCount
                  />
                </Form.Item>
              </section>

              <Divider />

              <section className="ai-training-prompt-section">
                <Title level={5}>Trợ lý phân tích điều hành</Title>
                <Text type="secondary" className="ai-training-helper-text">
                  Dưới đây là các ký hiệu và biến được sử dụng trong prompt cho trợ lý phân tích điều hành.
                </Text>
                <div className="ai-training-prompt-reference">
                  <div className="ai-training-prompt-reference-title">
                    Bảng chú giải cú pháp báo cáo
                  </div>
                  <div className="ai-training-prompt-reference-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Loại</th>
                          <th>Ký hiệu</th>
                          <th>Diễn giải</th>
                        </tr>
                      </thead>
                      <tbody>
                        {OPERATIONAL_PROMPT_REFERENCE_ROWS.map((item) => (
                          <tr key={`${item.group}-${item.syntax}`}>
                            <td>
                              <span className="ai-training-prompt-group">
                                {item.group}
                              </span>
                            </td>
                            <td>
                              <Text code>{item.syntax}</Text>
                            </td>
                            <td>{item.meaning}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Form.Item
                  name="operationalAssistantPrompt"
                  label="Nội dung System Prompt"
                  rules={[
                    {
                      required: true,
                      message:
                        "Vui lòng nhập prompt cho trợ lý phân tích điều hành",
                    },
                  ]}
                >
                  <TextArea
                    className="ai-training-prompt-editor"
                    autoSize={{ minRows: 20, maxRows: 32 }}
                    placeholder="Nhập prompt cho trợ lý phân tích điều hành..."
                    maxLength={10000}
                    showCount
                  />
                </Form.Item>
              </section>

              <Divider />

              <div className="ai-training-form-actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={promptingSaving}
                  disabled={!canUpdate}
                >
                  Lưu thay đổi
                </Button>
              </div>
            </Form>
          </Spin>
        </Card>
      ) : null}

      {!isSuggestionsTab && !isPromptingTab ? (
        <>
          <Card
            className="ai-training-card"
            title={
              <span className="ai-training-card-title">
                <InboxOutlined /> Tải lên tài liệu huấn luyện
              </span>
            }
            extra={
              <Text type="secondary">Định dạng: {ACCEPTED_FILE_LABEL}</Text>
            }
          >
            {trainingSnapshot.hasPendingChanges ? (
              <PendingRetrainNotice />
            ) : null}
            <div className="ai-training-card-split">
              <div className="ai-training-panel ai-training-upload-panel">
                <Alert
                  className="ai-training-secret-warning"
                  type="error"
                  showIcon
                  message="NGHIÊM CẤM đưa tài liệu mật, tài liệu thuộc danh mục bí mật nhà nước vào hệ thống. Cán bộ upload chịu trách nhiệm cá nhân về nội dung tài liệu."
                />
                <Dragger
                  className="ai-training-uploader"
                  accept={ACCEPTED_FILE_TYPES}
                  multiple
                  fileList={fileList}
                  beforeUpload={() => false}
                  onChange={handleFilesChange}
                  onRemove={(file) => {
                    const nextFileList = fileList.filter(
                      (item) => item.uid !== file.uid,
                    );
                    setFileList(nextFileList);
                    if (!nextFileList.length) {
                      setUploadCommitmentChecked(false);
                    }
                  }}
                  disabled={!canCreate || uploading}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Kéo thả file hoặc bấm chọn</p>
                  <div className="ai-training-upload-select-action">
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      disabled={!canCreate || uploading}
                    >
                      Chọn tài liệu
                    </Button>
                  </div>
                  <p className="ant-upload-hint">
                    {ACCEPTED_FILE_LABEL} · tối đa {MAX_UPLOAD_FILES} file/lần ·
                    file trùng tên sẽ thay thế bản cũ
                  </p>
                </Dragger>
                {fileList.length > 0 && (
                  <div className="ai-training-upload-commitment">
                    <Checkbox
                      checked={uploadCommitmentChecked}
                      disabled={!canCreate || uploading}
                      onChange={(event) =>
                        setUploadCommitmentChecked(event.target.checked)
                      }
                    >
                      Tôi xác nhận tài liệu này không thuộc danh mục bí mật nhà
                      nước và không chứa thông tin nghiệp vụ nhạy cảm chưa được
                      phép phổ biến.
                    </Checkbox>
                  </div>
                )}
                <div
                  className={`ai-training-upload-actions${
                    fileList.length ? "" : " is-empty"
                  }`}
                >
                  <Text type="secondary">
                    {fileList.length
                      ? `Đã chọn ${fileList.length} tài liệu`
                      : "Chưa chọn tài liệu"}
                  </Text>
                  <Button
                    type="primary"
                    icon={uploading ? <LoadingOutlined /> : <UploadOutlined />}
                    loading={uploading}
                    disabled={
                      !canCreate || !fileList.length || !uploadCommitmentChecked
                    }
                    onClick={handleUpload}
                  >
                    Tải tài liệu lên
                  </Button>
                </div>
              </div>

              <div className="ai-training-panel ai-training-list-panel">
                <div className="ai-training-section-heading">
                  <span>Tài liệu hiện có ({sources.length})</span>
                  <Space size={4} wrap>
                    <Button
                      type="primary"
                      icon={
                        retraining ? (
                          <LoadingOutlined />
                        ) : (
                          <ThunderboltOutlined />
                        )
                      }
                      loading={retraining}
                      onClick={handleRetrain}
                      disabled={!canUpdate}
                    >
                      Huấn luyện lại
                    </Button>
                    <Button
                      type="text"
                      icon={<ReloadOutlined />}
                      onClick={() => loadOverview(notebookType, historyPage)}
                      loading={loading}
                    >
                      Làm mới
                    </Button>
                  </Space>
                </div>
                {loading ? (
                  <div className="ai-training-loading">
                    <Spin />
                  </div>
                ) : sources.length ? (
                  <>
                    <List
                      className="ai-training-source-list"
                      dataSource={visibleSources}
                      renderItem={(source) => (
                        <List.Item
                          actions={[
                            <Popconfirm
                              key="delete"
                              title="Xóa tài liệu khỏi kho kiến thức AI?"
                              okText="Xóa"
                              cancelText="Hủy"
                              onConfirm={() => handleDeleteSource(source)}
                            >
                              <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                disabled={!canDelete}
                              />
                            </Popconfirm>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={getFileIcon(sourceDisplayName(source))}
                            title={sourceDisplayName(source)}
                            description={
                              <Space size={6} wrap>
                                {source.size ? (
                                  <Text type="secondary">
                                    {formatFileSize(source.size)}
                                  </Text>
                                ) : null}
                                <Text type="secondary">
                                  Tải lên bởi{" "}
                                  {source.uploadedByName || "Hệ thống"}
                                </Text>
                                <Text type="secondary">
                                  ·{" "}
                                  {formatDate(
                                    source.uploadedAt || source.createdAt,
                                  )}
                                </Text>
                                {source.status === "processing" ? (
                                  <Tag color="processing">Đang xử lý</Tag>
                                ) : null}
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                    {sources.length > LIST_PAGE_SIZE && (
                      <div className="ai-training-list-pagination">
                        <Pagination
                          size="small"
                          current={sourcePage}
                          pageSize={LIST_PAGE_SIZE}
                          total={sources.length}
                          showSizeChanger={false}
                          onChange={setSourcePage}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Chưa có tài liệu training"
                  />
                )}
              </div>
            </div>
          </Card>

          <Card
            className="ai-training-card"
            title={
              <span className="ai-training-card-title">
                <QuestionCircleOutlined /> Câu hỏi thường gặp (FAQ)
              </span>
            }
          >
            {trainingSnapshot.hasPendingChanges ? (
              <PendingRetrainNotice />
            ) : null}
            <div className="ai-training-card-split ai-training-faq-split">
              <div className="ai-training-panel ai-training-faq-form-panel">
                <Form
                  form={faqForm}
                  layout="vertical"
                  onFinish={handleAddFaq}
                  disabled={!canCreate}
                >
                  <Form.Item
                    name="question"
                    label="Câu hỏi"
                    rules={[
                      { required: true, message: "Vui lòng nhập câu hỏi" },
                    ]}
                  >
                    <Input
                      placeholder={`Nhập câu hỏi thường gặp cho ${meta.label}...`}
                      maxLength={500}
                    />
                  </Form.Item>
                  <Form.Item
                    name="answer"
                    label="Câu trả lời"
                    rules={[
                      { required: true, message: "Vui lòng nhập câu trả lời" },
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder="Nhập nội dung trả lời..."
                      maxLength={5000}
                      showCount
                    />
                  </Form.Item>
                  <div className="ai-training-form-actions">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<PlusOutlined />}
                      disabled={!canCreate}
                    >
                      Thêm FAQ
                    </Button>
                  </div>
                </Form>
              </div>

              <div className="ai-training-panel ai-training-faq-list-panel">
                <div className="ai-training-section-heading">
                  <span>FAQ hiện có ({faqs.length})</span>
                  <Space size={4} wrap>
                    <Button
                      type="primary"
                      icon={
                        retraining ? (
                          <LoadingOutlined />
                        ) : (
                          <ThunderboltOutlined />
                        )
                      }
                      loading={retraining}
                      onClick={handleRetrain}
                      disabled={!canUpdate}
                    >
                      Huấn luyện lại
                    </Button>
                  </Space>
                </div>
                <div className="ai-training-faq-list">
                  <div
                    ref={faqListRef}
                    className="ai-training-faq-list-scroll"
                  >
                    {faqs.length ? (
                      visibleFaqs.map((faq) => {
                        const faqId = faq.id || faq._id;

                        return (
                          <div className="ai-training-faq-item" key={faqId}>
                            <div className="ai-training-faq-content">
                              <Text strong>{faq.question}</Text>
                              <Popover
                                overlayClassName="ai-training-faq-detail-popover"
                                placement="topLeft"
                                trigger={["hover", "click"]}
                                title={faq.question}
                                content={
                                  <div className="ai-training-faq-detail">
                                    {faq.answer || "Không có nội dung"}
                                  </div>
                                }
                              >
                                <button
                                  type="button"
                                  className="ai-training-faq-answer-preview"
                                  aria-label={`Xem chi tiết FAQ: ${faq.question}`}
                                >
                                  <span className="ai-training-faq-answer">
                                    {faq.answer}
                                  </span>
                                  <span className="ai-training-faq-more">
                                    Xem thêm
                                  </span>
                                </button>
                              </Popover>
                              <Text type="secondary">
                                Thêm bởi {faq.createdByName || "Hệ thống"} ·{" "}
                                {formatDate(faq.createdAt)}
                              </Text>
                            </div>
                            <div className="ai-training-faq-actions">
                              <Popconfirm
                                title="Xóa FAQ này?"
                                okText="Xóa"
                                cancelText="Hủy"
                                onConfirm={() => handleDeleteFaq(faq)}
                              >
                                <Button
                                  danger
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  disabled={!canDelete}
                                />
                              </Popconfirm>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Chưa có FAQ"
                      />
                    )}
                  </div>
                  {faqs.length > LIST_PAGE_SIZE && (
                    <div className="ai-training-list-pagination">
                      <Pagination
                        size="small"
                        current={faqPage}
                        pageSize={LIST_PAGE_SIZE}
                        total={faqs.length}
                        showSizeChanger={false}
                        onChange={handleFaqPageChange}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <Card className="ai-training-status-card">
            <div className="ai-training-status-main">
              <div>
                <Title level={5}>
                  <ThunderboltOutlined /> Huấn luyện lại {meta.label}
                </Title>
                <Text type="secondary">
                  Gộp toàn bộ tài liệu và FAQ hiện có thành kiến thức nền để AI
                  sử dụng khi trả lời.
                </Text>
                <div className="ai-training-version-row">
                  <div className="ai-training-version">
                    <CheckCircleOutlined /> Phiên bản v
                    {trainingSnapshot.version || 0}
                    {trainingSnapshot.trainedAt
                      ? ` · Cập nhật lúc ${formatDate(trainingSnapshot.trainedAt)}`
                      : " · Chưa huấn luyện"}
                  </div>
                  {trainingSnapshot.hasPendingChanges ? (
                    <Tag color="warning">Có thay đổi chờ huấn luyện</Tag>
                  ) : null}
                </div>
              </div>
              <Button
                type="primary"
                icon={
                  retraining ? <LoadingOutlined /> : <ThunderboltOutlined />
                }
                loading={retraining}
                onClick={handleRetrain}
                disabled={!canUpdate}
              >
                Huấn luyện lại
              </Button>
            </div>
          </Card>

          <Card
            className="ai-training-card ai-training-history-card"
            title={
              <span className="ai-training-card-title">
                <ClockCircleOutlined /> Lịch sử training
              </span>
            }
          >
            {loading ? (
              <div className="ai-training-history-loading">
                <Spin tip="Đang tải lịch sử training..." />
              </div>
            ) : history.length ? (
              <List
                className="ai-training-history-list"
                dataSource={history}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <CheckCircleOutlined className="ai-training-history-icon" />
                      }
                      title={
                        <Space>
                          <Text>{item.actorName || "Hệ thống"}</Text>
                          <Tag
                            color={
                              item.action === "retrain"
                                ? "orange"
                                : item.action === "faq"
                                  ? "green"
                                  : "blue"
                            }
                          >
                            {item.actionLabel || item.action}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space size={6}>
                          <Text type="secondary">
                            {formatDate(item.createdAt)}
                          </Text>
                          <Text type="secondary">· {item.detail}</Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Chưa có lịch sử training"
              />
            )}
            {historyTotal > historyPageSize && (
              <div className="ai-training-history-pagination">
                <Pagination
                  size="small"
                  current={historyPage}
                  pageSize={historyPageSize}
                  total={historyTotal}
                  showSizeChanger={false}
                  onChange={(page) => {
                    setHistoryPage(page);
                    loadOverview(notebookType, page);
                  }}
                />
              </div>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default AiTraining;
